import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { LogOut, Search, Filter, Loader2, FileX, HardHat, Users, UserPlus } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [updates, setUpdates] = useState([]);
  const [tradies, setTradies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filterTradie, setFilterTradie] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchUpdates();
  }, [filterTradie, filterStatus, filterCategory, searchQuery]);

  const fetchData = async () => {
    try {
      const [updatesRes, tradiesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/updates`),
        axios.get(`${API_URL}/admin/tradies`)
      ]);
      setUpdates(updatesRes.data);
      setTradies(tradiesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpdates = async () => {
    try {
      const params = new URLSearchParams();
      if (filterTradie && filterTradie !== 'all') params.append('tradie_id', filterTradie);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      if (filterCategory && filterCategory !== 'all') params.append('category', filterCategory);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API_URL}/admin/updates?${params.toString()}`);
      setUpdates(response.data);
    } catch (error) {
      console.error('Failed to fetch updates:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'New': return 'status-new';
      case 'In Progress': return 'status-in-progress';
      case 'Done': return 'status-done';
      default: return 'status-new';
    }
  };

  const getUrgencyClass = (urgency) => {
    switch (urgency) {
      case 'Low': return 'urgency-low';
      case 'Medium': return 'urgency-medium';
      case 'High': return 'urgency-high';
      default: return 'urgency-low';
    }
  };

  const getCategoryClass = (category) => {
    switch (category) {
      case 'Win Work': return 'category-win-work';
      case 'Deliver Work': return 'category-deliver-work';
      case 'Get Paid': return 'category-get-paid';
      default: return '';
    }
  };

  const clearFilters = () => {
    setFilterTradie('all');
    setFilterStatus('all');
    setFilterCategory('all');
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-slate-100 page-enter">
      {/* Header */}
      <header className="bg-[#1F2A37] text-white p-4 shadow-lg sticky top-0 z-20">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <HardHat className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">2Tap</h1>
              <p className="text-sm text-slate-300">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              data-testid="setup-requests-btn"
              onClick={() => navigate('/admin/setup-requests')}
              className="text-slate-300 hover:text-white hover:bg-slate-700 hidden sm:flex items-center gap-2"
            >
              <UserPlus className="w-4 h-4" />
              Setup Requests
            </Button>
            <span className="text-sm text-slate-300 hidden sm:block">{user?.name}</span>
            <Button
              variant="ghost"
              size="icon"
              data-testid="logout-btn"
              onClick={handleLogout}
              className="text-slate-300 hover:text-white hover:bg-slate-700"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 max-w-7xl mx-auto">
        {/* Filters */}
        <Card className="border-2 border-slate-200 p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-5 h-5 text-slate-500" />
            <span className="font-semibold text-slate-700">Filters</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {/* Search */}
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="search-input"
                placeholder="Search job, address, keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-11"
              />
            </div>

            {/* Filter by Tradie */}
            <Select value={filterTradie} onValueChange={setFilterTradie}>
              <SelectTrigger data-testid="filter-tradie-select" className="h-11">
                <Users className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="All Tradies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tradies</SelectItem>
                {tradies.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Filter by Status */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="filter-status-select" className="h-11">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Done">Done</SelectItem>
              </SelectContent>
            </Select>

            {/* Filter by Category */}
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger data-testid="filter-category-select" className="h-11">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Win Work">Win Work</SelectItem>
                <SelectItem value="Deliver Work">Deliver Work</SelectItem>
                <SelectItem value="Get Paid">Get Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(filterTradie !== 'all' || filterStatus !== 'all' || filterCategory !== 'all' || searchQuery) && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="clear-filters-btn"
              onClick={clearFilters}
              className="mt-3 text-orange-600 hover:text-orange-700"
            >
              Clear all filters
            </Button>
          )}
        </Card>

        {/* Results */}
        <Card className="border-2 border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : updates.length === 0 ? (
            <div className="text-center py-20">
              <FileX className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-slate-600">No Updates Found</h3>
              <p className="text-slate-500 mt-2">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Date/Time</TableHead>
                    <TableHead className="font-semibold">Tradie</TableHead>
                    <TableHead className="font-semibold">Job Name</TableHead>
                    <TableHead className="font-semibold">Category</TableHead>
                    <TableHead className="font-semibold">Urgency</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {updates.map((update) => (
                    <TableRow
                      key={update.id}
                      data-testid={`update-row-${update.id}`}
                      className="cursor-pointer hover:bg-orange-50 transition-colors"
                    >
                      <TableCell 
                        className="font-mono text-sm text-slate-600"
                        onClick={() => navigate(`/admin/update/${update.id}`)}
                      >
                        {format(new Date(update.created_at), 'MMM d, h:mm a')}
                      </TableCell>
                      <TableCell 
                        className="font-medium"
                        onClick={() => navigate(`/admin/update/${update.id}`)}
                      >
                        {update.tradie_name}
                      </TableCell>
                      <TableCell 
                        className="max-w-[200px] truncate"
                        onClick={() => navigate(`/admin/update/${update.id}`)}
                      >
                        {update.job_name}
                      </TableCell>
                      <TableCell onClick={() => navigate(`/admin/update/${update.id}`)}>
                        <span className={`status-badge ${getCategoryClass(update.category)}`}>
                          {update.category}
                        </span>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/admin/update/${update.id}`)}>
                        <span className={`status-badge ${getUrgencyClass(update.urgency)}`}>
                          {update.urgency}
                        </span>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/admin/update/${update.id}`)}>
                        <span className={`status-badge ${getStatusClass(update.status)}`}>
                          {update.status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        {/* Stats summary */}
        {!loading && updates.length > 0 && (
          <div className="mt-4 text-sm text-slate-500 text-center">
            Showing {updates.length} update{updates.length !== 1 ? 's' : ''}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
